import React, { useEffect, useState } from "react";
import axios from "axios";
import DynamicRegistrationForm from "./DynamicRegistrationForm";
import {
  getAnswerDetails,
  getVisibleAnswers,
  validateFormAnswers,
} from "./schemaRuntime";
import "./previewRegistration.css";

const steps = ["Session", "Quantity", "Form", "Confirmation", "Payment", "Success", "My Ticket"];

const PreviewRegistration = () => {
  const token = new URLSearchParams(window.location.search).get("token");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [step, setStep] = useState(0);
  const [language, setLanguage] = useState("ind");
  const [device, setDevice] = useState("mobile");
  const [sessionId, setSessionId] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [answers, setAnswers] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [validation, setValidation] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);

  useEffect(() => {
    if (!token) {
      setError("Preview token is missing.");
      setLoading(false);
      return;
    }
    axios
      .get(`${process.env.REACT_APP_API_URL}form-preview/${encodeURIComponent(token)}`)
      .then((response) => {
        const data = response.data.data;
        setPreview(data);
        const sessions = (data.context.event && data.context.event.sub_event) || [];
        if (sessions.length) setSessionId(String(sessions[0].id));
      })
      .catch((requestError) =>
        setError(
          (requestError.response && requestError.response.data && requestError.response.data.message) ||
            "Unable to load preview."
        )
      )
      .finally(() => setLoading(false));
  }, [token]);

  const event = preview && preview.context ? preview.context.event : null;
  const definition = preview ? preview.definition : null;
  const sessions = (event && event.sub_event) || [];
  const session = sessions.find((item) => String(item.id) === String(sessionId)) || null;

  const updateAnswer = (key, value) => {
    setAnswers((current) => ({ ...current, [key]: value }));
    setFieldErrors((current) => ({ ...current, [key]: undefined }));
  };

  const validateWithApi = async () => {
    const clientValidation = validateFormAnswers(definition, session, answers, language);
    if (!clientValidation.valid) {
      setFieldErrors(clientValidation.errors);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}form-preview/${encodeURIComponent(token)}/validate`,
        {
          church_event_id: session.id,
          quantity: Number(quantity),
          answers: getVisibleAnswers(definition, session, answers, language),
          form_template_version_id: definition.version_id,
        }
      );
      setValidation(response.data.data);
      setStep(3);
    } catch (requestError) {
      const message = requestError.response && requestError.response.data
        ? requestError.response.data.message
        : "Preview validation failed.";
      if (message && typeof message === "object") {
        const mapped = {};
        Object.keys(message).forEach((key) => {
          const fieldKey = key.replace(/^extras\./, "");
          mapped[fieldKey] = Array.isArray(message[key]) ? message[key][0] : String(message[key]);
        });
        setFieldErrors(mapped);
        setError("Please review the highlighted fields.");
      } else {
        setError(String(message));
      }
    } finally {
      setLoading(false);
    }
  };

  const next = () => {
    if (step === 0 && !session) return;
    if (step === 1 && (!quantity || Number(quantity) < 1)) return;
    if (step === 2) {
      validateWithApi();
      return;
    }
    if (step === 3 && event && event.TNC && !termsAccepted) {
      setError(language === "en" ? "Accept the terms and conditions to continue." : "Setujui syarat dan ketentuan untuk melanjutkan.");
      return;
    }
    setError("");
    setStep((current) => Math.min(current + 1, steps.length - 1));
  };

  if (loading && !preview) return <div className="preview-registration__state">Loading preview...</div>;
  if (error && !preview) return <div className="preview-registration__state preview-registration__state--error">{error}</div>;

  const details = getAnswerDetails(definition, session, answers, language, "ticket");
  return (
    <div className="preview-registration">
      <div className="preview-registration__watermark">PREVIEW - No booking will be created</div>
      <header className="preview-registration__toolbar">
        <div>
          <strong>Preview Registration Form</strong>
          <span>{preview.mode === "event" ? event.name : definition.template_name}</span>
        </div>
        <div className="preview-registration__toolbar-actions">
          <button type="button" className={device === "mobile" ? "active" : ""} onClick={() => setDevice("mobile")}>Mobile</button>
          <button type="button" className={device === "desktop" ? "active" : ""} onClick={() => setDevice("desktop")}>Desktop</button>
          <button type="button" className={language === "ind" ? "active" : ""} onClick={() => setLanguage("ind")}>Indonesia</button>
          <button type="button" className={language === "en" ? "active" : ""} onClick={() => setLanguage("en")}>English</button>
        </div>
      </header>

      <main className={`preview-registration__viewport preview-registration__viewport--${device}`}>
        {event.image_url ? <img className="preview-registration__cover" src={event.image_url} alt={event.name} /> : null}
        <div className="preview-registration__steps">
          {steps.map((label, index) => <span key={label} className={index === step ? "active" : index < step ? "done" : ""}>{index + 1}</span>)}
        </div>
        <section className="preview-registration__content">
          <small>Step {step + 1} of {steps.length}</small>
          <h2>{steps[step]}</h2>
          {error ? <div className="preview-registration__error">{String(error)}</div> : null}

          {step === 0 ? (
            <div className="preview-registration__sessions">
              {sessions.map((item) => (
                <button key={item.id} type="button" className={String(item.id) === String(sessionId) ? "active" : ""} onClick={() => { setSessionId(String(item.id)); setAnswers({}); setTermsAccepted(false); }}>
                  <strong>{item.name}</strong><span>{item.quantity} available</span>
                </button>
              ))}
            </div>
          ) : null}

          {step === 1 ? (
            <label className="preview-registration__quantity">Quantity<input type="number" min="1" max={session.max_ticket || 1} value={quantity} onChange={(e) => setQuantity(e.target.value)} /></label>
          ) : null}

          {step === 2 ? <DynamicRegistrationForm definition={definition} session={session} answers={answers} onChange={updateAnswer} language={language} errors={fieldErrors} /> : null}

          {step === 3 ? (
            <div className="preview-registration__summary">
              <p><strong>{event.name}</strong></p><p>{session.name}</p><p>Quantity: {quantity}</p>
              {details.map((detail) => <p key={detail.key}><span>{detail.label}</span><strong>{String(detail.value)}</strong></p>)}
              {event.TNC ? (
                <div className="preview-registration__terms">
                  <h3>{language === "en" ? "Terms and Conditions" : "Syarat dan Ketentuan"}</h3>
                  <div dangerouslySetInnerHTML={{ __html: event.TNC }} />
                  <label>
                    <input type="checkbox" checked={termsAccepted} onChange={(changeEvent) => setTermsAccepted(changeEvent.target.checked)} />
                    {language === "en" ? "I agree to the terms and conditions." : "Saya menyetujui syarat dan ketentuan."}
                  </label>
                </div>
              ) : null}
            </div>
          ) : null}

          {step === 4 && validation ? (
            <div className="preview-registration__summary"><p><span>Unit price</span><strong>IDR {Number(validation.summary.unit_price).toLocaleString()}</strong></p><p><span>Total</span><strong>IDR {Number(validation.summary.total_price).toLocaleString()}</strong></p><div className="preview-registration__notice">No payment API will be called.</div></div>
          ) : null}

          {step === 5 ? <div className="preview-registration__success"><strong>Registration simulated successfully</strong><span>No booking, quota, log, or payment was created.</span></div> : null}

          {step === 6 && validation ? (
            <div className="preview-registration__ticket"><small>MY TICKET</small><h3>{event.name}</h3><p>{session.name}</p><div>{validation.ticket.code}</div><strong>{validation.ticket.participant_name}</strong></div>
          ) : null}
        </section>
        <footer className="preview-registration__footer">
          <button type="button" disabled={step === 0 || loading} onClick={() => setStep((current) => Math.max(0, current - 1))}>Back</button>
          {step < steps.length - 1 ? <button type="button" className="primary" disabled={loading} onClick={next}>{loading ? "Validating..." : "Continue"}</button> : null}
        </footer>
      </main>
    </div>
  );
};

export default PreviewRegistration;
